import { toast } from 'sonner'
import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view'
import { widthAndHeightToAspectRatio } from '@basehub/utils/math/index'
import { getFileHashSHA256 } from '@basehub/data/blob/file-utils'
import {
  productionAssetsURL,
  productionR2Origin,
} from '@basehub/shared-constants'
import { uploadFileToRepo } from '~/lib/file-utils'

const uploadKey = new PluginKey('upload-video')

export const UploadVideosPlugin = () =>
  new Plugin({
    key: uploadKey,
    state: {
      init() {
        return DecorationSet.empty
      },
      apply(tr, set) {
        set = set.map(tr.mapping, tr.doc)
        // See if the transaction adds or removes any placeholders
        const action = tr.getMeta(uploadKey)
        if (action && action.add) {
          const { id, pos, src, width, height } = action.add

          const nodeWrapper = document.createElement('div')
          nodeWrapper.setAttribute(
            'class',
            'react-renderer node-video rt-video'
          )

          const placeholder = document.createElement('figure')
          placeholder.style.display = 'flex'
          placeholder.style.flexDirection = 'column'
          placeholder.style.whiteSpace = 'normal'

          // imagen simulando el poster del video
          const video = document.createElement('video')
          video.src = src
          video.poster = src
          video.width = width
          video.height = height
          video.style.width = '100%'
          video.style.height = 'auto'
          video.style.maxHeight = '500px'
          video.style.objectFit = 'contain'
          video.style.borderRadius = '2px'
          video.style.opacity = '0.6'
          video.classList.add('animate-pulse')
          video.style.aspectRatio = `${width} / ${height}`

          // add class to hide the controls

          video.style.aspectRatio = `${width} / ${height}`

          const figcaption = document.createElement('textarea')
          figcaption.placeholder = 'Caption...'
          figcaption.style.height = '26px'
          figcaption.style.width = '100%'
          figcaption.style.textAlign = 'center'
          figcaption.style.padding = '0.5em'
          // opacity
          figcaption.style.opacity = '0'
          figcaption.style.pointerEvents = 'none'
          figcaption.readOnly = true

          placeholder.appendChild(video)
          placeholder.appendChild(figcaption)
          nodeWrapper.appendChild(placeholder)
          const deco = Decoration.widget(Math.max(pos, 0), nodeWrapper, {
            id,
          })
          set = set.add(tr.doc, [deco])
        } else if (action && action.remove) {
          set = set.remove(
            set.find(
              undefined,
              undefined,
              (spec) => spec.id == action.remove.id
            )
          )
        }
        return set
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)
      },
    },
  })

function findPlaceholder(state: EditorState, id: object) {
  const decos = uploadKey.getState(state)
  const found = decos.find(null, null, (spec: { id: object }) => spec.id == id)
  return found.length ? found[0].from : null
}

export function startVideoUpload(
  file: File,
  view: EditorView,
  pos: number,
  userData: { repoId: string; teamId: string },
  options?:
    | { width?: number; height?: number; thumbnail?: Blob | undefined }
    | undefined
) {
  if (!file.type.includes('video/')) {
    toast.error('File type not supported.')
    return
  }

  const id = {}
  const tr = view.state.tr
  if (!tr.selection.empty) tr.deleteSelection()

  // read thumbnail (Blob) to url
  const thumbnailUrl = options?.thumbnail
    ? URL.createObjectURL(options?.thumbnail)
    : null
  // Insert placeholder node
  tr.setMeta(uploadKey, {
    add: {
      id,
      src: thumbnailUrl,
      ...(options ?? {}),
      pos,
    },
  })
  view.dispatch(tr)

  handleVideoUpload(file, {
    ...userData,
    width: options?.width,
    height: options?.height,
  })
    .then(({ src, width, height, aspectRatio }) => {
      const { schema } = view.state

      const pos = findPlaceholder(view.state, id)
      // If the content around the placeholder has been deleted, drop
      // the video

      if (pos == null) return

      // Otherwise, insert it at the placeholder's position, and remove
      // the placeholder

      // When BLOB_READ_WRITE_TOKEN is not valid or unavailable, read
      // the video locally
      if (typeof src !== 'string') throw new Error('Invalid Video')
      const videoSrc = src.replace(productionR2Origin, productionAssetsURL)

      if (!schema.nodes.video) return

      const node = schema.nodes.video.create({
        src: videoSrc,
        width,
        height,
        aspectRatio,
        poster: options?.thumbnail,
      })
      const transaction = view.state.tr
        .replaceWith(pos === 1 ? 0 : pos, pos === 1 ? 0 : pos, node)
        .setMeta(uploadKey, { remove: { id } })
      view.dispatch(transaction)
    })
    .catch(() => {
      // If the upload fails, remove the placeholder
      const pos = findPlaceholder(view.state, id)
      if (pos == null) return
      setTimeout(() => {
        const transaction = view.state.tr.setMeta(uploadKey, { remove: { id } })
        view.dispatch(transaction)
      }, 500)
    })
    .finally(() => {
      // clean all the previous states, ALL
      // revoke url
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl)
      }
    })
}

export const handleVideoUpload = (
  file: File,
  {
    repoId,
    teamId,
    width,
    height,
  }: {
    repoId: string
    teamId: string
    width?: number
    height?: number
  }
) => {
  return new Promise<{
    src: string
    width: number
    height: number
    aspectRatio: string
  }>((resolve, reject) => {
    toast.promise(
      async () => {
        const res = await uploadFileToRepo(file, {
          repoId,
          teamId,
          fileHash: await getFileHashSHA256(file),
        })

        if (width && height) {
          resolve({
            src: res.uploadURL,
            width,
            height,
            aspectRatio: widthAndHeightToAspectRatio(width, height),
          })
        } else {
          const video = document.createElement('video')
          const onVideoLoad = () => {
            resolve({
              src: res.uploadURL,
              width: video.videoWidth,
              height: video.videoHeight,
              aspectRatio: widthAndHeightToAspectRatio(
                video.videoWidth,
                video.videoHeight
              ),
              // htmlvideo params
            })
            video.removeEventListener('loadedmetadata', onVideoLoad)
            video.remove()
            URL.revokeObjectURL(video.src)
          }
          video.addEventListener('loadedmetadata', onVideoLoad)
          video.src = res.uploadURL
        }
      },
      {
        loading: 'Uploading video...',
        success: 'Video uploaded successfully.',
        error: (e) => {
          reject()
          return e.message
        },
      }
    )
  })
}
